import React from 'react';

import { Badge, Card, Tooltip, Link } from '@ergeon/core-components';
import { UserCircleIcon, PhoneIcon, EnvelopeIcon } from '@ergeon/icons/hero/outline';

import { showUpcomingFeatures } from '../../../utils';
import { formatPhoneNumber } from '../utils/formatUtils';

type Customer = {
  id: number;
  full_name: string;
  phone_number: string;
  email: string;
  unread_mentions_count?: number;
  ud_unread_count?: number;
};

type CustomerItemProps = {
  customer: Customer;
  activitiesCount: number;
  hasOverdueActivities?: boolean;
  href: string;
  incrementalUnread?: number;
};

const CustomerItem: React.FC<CustomerItemProps> = ({
  customer,
  activitiesCount,
  hasOverdueActivities = false,
  href,
  incrementalUnread = 0,
}) => {
  const mockMentionsCount = 0; // Mock data - replace with customer.unread_mentions_count when API is ready
  const showMentionsBadge = showUpcomingFeatures('APPS-5873') && mockMentionsCount > 0;
  const showActivitiesBadge = activitiesCount > 0;
  const totalUnread = (customer.ud_unread_count || 0) + incrementalUnread;
  const showUnreadBadge = totalUnread > 0;
  const activitiesBadgeFlavor = hasOverdueActivities ? 'danger' : 'primary';
  const activitiesTooltipMsg = hasOverdueActivities ?
    `${activitiesCount} overdue ${activitiesCount === 1 ? 'activity' : 'activities'}` :
    `${activitiesCount} ${activitiesCount === 1 ? 'activity' : 'activities'} due today`;

  return (
    <Link className="contents [&>span]:contents" href={href} unstyled>
      <Card
        className="flex lg:flex-row flex-col justify-between lg:items-center gap-2 p-4"
        hasHover
        hoverType="outline"
        zIndex="2">
        <div className="flex items-center gap-2">
          <UserCircleIcon className="size-6 text-gray-500 dark:text-gray-600 shrink-0" />
          {customer.full_name && (
            <span
              className={`text-gray-900 dark:text-white truncate additional-4 ${showUnreadBadge ? 'font-bold' : ''}`}>
              {customer.full_name}
            </span>
          )}
          {showActivitiesBadge && (
            <Tooltip msg={activitiesTooltipMsg} position="right">
              <Badge flavor={activitiesBadgeFlavor} value={activitiesCount} />
            </Tooltip>
          )}
          {showUnreadBadge && (
            <Tooltip msg={`${totalUnread} unread`} position="right">
              <Badge flavor="warning" value={totalUnread} />
            </Tooltip>
          )}
        </div>
        <div className="flex flex-grow justify-between lg:justify-end items-center gap-6 w-full">
          {customer.email && (
            <div className="flex items-center gap-1">
              <EnvelopeIcon className="size-3.5 text-gray-500 dark:text-gray-600 shrink-0" />
              <span className="text-gray-700 dark:text-gray-400 truncate paragraph-3">{customer.email}</span>
            </div>
          )}
          {customer.phone_number && (
            <div className="flex items-center gap-1">
              <PhoneIcon className="size-3.5 text-gray-500 dark:text-gray-600 shrink-0" />
              <span className="text-gray-700 dark:text-gray-400 truncate paragraph-3">
                {formatPhoneNumber(customer.phone_number)}
              </span>
            </div>
          )}
        </div>

        {showMentionsBadge && <Badge flavor="primary" value={mockMentionsCount} />}
      </Card>
    </Link>
  );
};

export default CustomerItem;
